import icn_linebar_chart from "assets/images/icons/icn_linebar_chart.svg";
import Diagnosis from "assets/images/icons/btn_diagnosis.png";

import React, { useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { Box, Typography } from "@mui/material";
import { createColumnHelper } from "@tanstack/react-table";

import useTableInstance from "hooks/useTableInstance";

import CollapseCard from "components/Collapse";
import TableField from "components/Table";
import WiredCableQualityChart from "pages/AInsight/LinkIntegrity/charts/WiredCableQualityChart";

const WiredCableQualityDrawer = ({
  wiredCableQualityChartSeriesData,
  wiredCableQualityApiChartData,
  wiredCableQualityPortList,
  auth,
}) => {
  const theme = useTheme();
  const columnHelper = useMemo(() => createColumnHelper(), []);

  const tableData = wiredCableQualityPortList?.errorPortList || [];

  const wiredCableQualityColumns = useMemo(
    () => [
      columnHelper.accessor("deviceName", {
        header: "Device Name",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("portId", {
        header: "Port ID",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("crcErrorNum", {
        header: "CRC Errors (24h)",
        cell: (info) => info.getValue(),
      }),
      /* columnHelper.accessor("currentSpeed", {
        header: "Link Speed",
        cell: (info) => info.getValue(),
      }), */ //先拿掉
    ],
    [columnHelper],
  );

  const wiredCableQualityColumnsTable = useTableInstance({
    tableName: "wiredCableQualityDrawerTable",
    columns: wiredCableQualityColumns,
    data: tableData,
    enableSorting: false,
    enablePagination: false,
  });

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box
          component="img"
          src={icn_linebar_chart}
          sx={{ width: 20, height: 20, mr: 1.5 }}
        />

        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontSize: theme.typography.customFontSize[3],
          }}
        >
          Wired Cable Quality
        </Typography>
      </Box>

      <CollapseCard title="24H CRC Error Trend">
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box sx={{ width: "100%" }}>
            <WiredCableQualityChart data={wiredCableQualityChartSeriesData} />
          </Box>

          <Box
            sx={{
              border: `1px solid ${theme.palette.primary.main}`,
              borderRadius: theme.shape.customBorderRadius[1],
              backgroundColor: theme.palette.primary.blueFourth,
              px: 2.5,
              py: 1.5,
            }}
          >
            <Typography
              sx={{
                fontSize: theme.typography.customFontSize[1],
                fontWeight: theme.typography.customFontWeight[3],
                color: theme.palette.primary.black,
                mb: 1,
              }}
            >
              Insight Bridge
            </Typography>

            <Typography
              sx={{
                fontSize: theme.typography.customFontSize[5],
                fontWeight: theme.typography.customFontWeight[3],
                color: theme.palette.primary.main,
                lineHeight: 1.2,
              }}
            >
              Noisy Ports: {wiredCableQualityApiChartData.noisyPorts}
            </Typography>

            <Typography
              sx={{
                fontSize: theme.typography.customFontSize[1],
                color: theme.palette.primary.black,
                mt: 0.5,
              }}
            >
              Detected via CRC/FCS Counter
            </Typography>
          </Box>

          <Box
            sx={{
              position: "relative",
              backgroundColor: theme.palette.primary.greyPrimaryTransparency,
              borderRadius: theme.shape.customBorderRadius[0],
              px: 2.5,
              py: 2.5,
              color: theme.palette.primary.white,
              "&::before": {
                content: '""',
                position: "absolute",
                top: "-7px",
                left: "110px",
                width: 0,
                height: 0,
                borderLeft: "7px solid transparent",
                borderRight: "7px solid transparent",
                borderBottom: `7px solid ${theme.palette.primary.greyPrimaryTransparency}`,
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
              <Box
                component="img"
                src={Diagnosis}
                sx={{ width: 24, height: 24, mr: 1.5 }}
              />

              <Typography
                sx={{
                  fontSize: theme.typography.customFontSize[3],
                  fontWeight: theme.typography.customFontWeight[3],
                  color: theme.palette.primary.white,
                }}
              >
                Diagnosis & Remediation
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: theme.typography.customFontSize[1],
                color: theme.palette.primary.white,
                lineHeight: 1.6,
                pl: 4,
                mb: 2,
              }}
            >
              High probability of physical layer degradation. The pattern of CRC
              errors correlates with peak traffic times, suggesting signal
              integrity issues rather than congestion drops.
            </Typography>

            <Box
              component="ul"
              sx={{
                m: 0,
                pl: 6,
                color: theme.palette.primary.white,
              }}
            >
              <Typography
                component="li"
                sx={{
                  fontSize: theme.typography.customFontSize[1],
                  lineHeight: 1.6,
                }}
              >
                Perform physical inspection of patch cables for crimp damage or
                sharp bends.
              </Typography>

              <Typography
                component="li"
                sx={{
                  fontSize: theme.typography.customFontSize[1],
                  lineHeight: 1.6,
                }}
              >
                Check for new EMI sources &#40;motors, fluorescent ballasts&#41;
                near cable runs.
              </Typography>
            </Box>
          </Box>

          <Box>
            <Box
              sx={{
                pb: 2,
                borderBottom: `1px solid ${theme.palette.primary.greyThread}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: theme.typography.customFontSize[3],
                  fontWeight: theme.typography.customFontWeight[3],
                  color: theme.palette.primary.main,
                }}
              >
                Physical Layer Errors
              </Typography>
            </Box>

            <TableField {...wiredCableQualityColumnsTable} />
          </Box>
        </Box>
      </CollapseCard>
    </Box>
  );
};

export default WiredCableQualityDrawer;
